// ============================================================================
// ENGAGEMENT CLOSURE HISTORY MODAL
// ============================================================================
// Read-only modal listing every close event on an engagement, ordered by
// closure_no DESC. Reversed closures render with a "Reopened" sub-line.
// At most one row is "Active" (current closure); all others are reversed.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useClosureHistory } from 'hooks/api/useEngagement';
import type { Engagement } from 'api/types';

interface EngagementClosureHistoryModalProps {
  isOpen: boolean;
  engagement: Engagement | null;
  onClose: () => void;
}

function formatDate(s?: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹ ${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const EngagementClosureHistoryModal: React.FC<EngagementClosureHistoryModalProps> = ({
  isOpen,
  engagement,
  onClose,
}) => {
  const { data: rows, loading } = useClosureHistory(engagement?.id || null, isOpen);

  if (!isOpen || !engagement) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Closure History &mdash; {engagement.code}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>
        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Loading closure history...
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              No closure history for this engagement.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'left', width: '50px' }}>#</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'left' }}>Closing Date</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'left' }}>Basis</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Standard (WIP)</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Logged (basis)</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Invoiced</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Variance</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'left' }}>Closed By</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const variance = c.varianceAmount;
                  return (
                    <React.Fragment key={c.id}>
                      <tr style={{ borderBottom: c.isActive ? '1px solid var(--border-color)' : '1px dashed var(--border-color)' }}>
                        <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{c.closureNo}</td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{formatDate(c.closingDate)}</td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{c.rateBasis}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{formatCurrency(c.loggedAmountStandard)}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{formatCurrency(c.loggedAmountAtBasis)}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{formatCurrency(c.invoicedAmount)}</td>
                        <td
                          style={{
                            padding: '0.4rem 0.5rem',
                            textAlign: 'right',
                            color: variance < 0 ? '#dc2626' : variance > 0 ? '#16a34a' : 'inherit',
                            fontWeight: 600,
                          }}
                        >
                          {formatCurrency(variance)}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{c.createdByName || '—'}</td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          {c.isActive ? (
                            <span className="badge badge-active">Active</span>
                          ) : (
                            <span className="badge badge-inactive">Reopened</span>
                          )}
                        </td>
                      </tr>
                      {!c.isActive && (
                        <tr>
                          <td colSpan={9} style={{
                            padding: '0.25rem 0.5rem 0.6rem 1.6rem',
                            fontSize: '0.72rem',
                            color: 'var(--text-muted)',
                            borderBottom: '1px solid var(--border-color)',
                          }}>
                            Reopened {formatDate(c.reversedAt)} by{' '}
                            <strong>{c.reversedByName || '—'}</strong>
                            {c.reversalReason ? ` — ${c.reversalReason}` : ''}
                            {c.invoiceReference ? ` (invoice ref: ${c.invoiceReference})` : ''}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default EngagementClosureHistoryModal;
